// @flow

export type CreditCardBrand =
  | 'American Express'
  | 'Diners Club'
  | 'Discover'
  | 'JCB'
  | 'MasterCard'
  | 'UnionPay'
  | 'Unknown'
  | 'Visa';

export type CreditCardBrandCode =
  | 'american-express'
  | 'diners-club'
  | 'discover'
  | 'jcb'
  | 'mastercard'
  | 'master-card'
  | 'unionpay'
  | 'visa';

export type ApiKeys = { [string]: string };

export type CreditCardDefinition = {
  code: {
    name: string,
    size: number,
  },
  gaps: number[],
  lengths: number[],
  niceType: string,
  type: string,
};

export type CreditCardFields = {
  definition: CreditCardDefinition,
  expiry: string,
  last4: string,
};

export type HostedFieldType = 'number' | 'expiry' | 'cvc';

export type HostedFieldToken = {
  details: {
    address_country: string,
    address_state: string,
    address_zip: string,
    brand: string,
    exp_month: string,
    exp_year: string,
    last4: string,
  },
  token: string,
};

export type HostedFieldAdditionalData = {
  addressCountry: string,
  addressState: string,
  addressZip: string,
  name: string,
};

export type HostedFieldValue = {
  brand?: ?CreditCardBrandCode,
  isEmpty: boolean,
  originalError: string,
};

export interface HostedFieldBackendInterface {
  createFields(
    style: any,
    onChange: (HostedFieldType, HostedFieldValue) => void
  ): Promise<void>;

  destroyFields(): void;

  createToken(
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken>;
}

export type SensitiveValue = HostedFieldValue;

export type FieldError = {|
  isFixed: boolean,
  message: string,
|};

export type TFieldState<TSensitive, TValue> = {|
  error: ?FieldError,
  isSensitive: TSensitive,
  prettyName: string,
  value: TValue,
|};

export type NormalFieldState = TFieldState<false, string>;
export type SensitiveFieldState = TFieldState<true, SensitiveValue>;

export type FieldState = NormalFieldState | SensitiveFieldState;

export type BankFormFields = {|
  accountNumber: NormalFieldState,
  accountType: NormalFieldState,
  businessName: NormalFieldState,
  extendedAddress: NormalFieldState,
  firstName: NormalFieldState,
  lastName: NormalFieldState,
  locality: NormalFieldState,
  ownershipType: NormalFieldState,
  postalCode: NormalFieldState,
  region: NormalFieldState,
  routingNumber: NormalFieldState,
  streetAddress: NormalFieldState,
|};

export type CreditCardFormFields = {|
  addressCountry: NormalFieldState,
  addressState: NormalFieldState,
  addressZip: NormalFieldState,
  cvc: SensitiveFieldState,
  expiry: SensitiveFieldState,
  name: NormalFieldState,
  number: SensitiveFieldState,
|};

export type Fields = Object;
export type FieldName = string;
export type FieldValue = string | SensitiveValue;

export type Validators<TState> = {
  [string]: (fieldState: TState, fields: Fields) => string,
};

export type State = {|
  fields: Fields,
  hasErrors: false,
  hasSubmitted: boolean,
  isDirty: boolean,
  isTokenizing: boolean,
  isValid: boolean,
  validators: Object,
|};

export const paymentSourceBackends = Object.freeze({
  braintree: 'braintree',
  stripe: 'stripe',
});

export const paymentSourceMethods = Object.freeze({
  card: 'card',
  paypal: 'paypal',
  bank: 'bank',
});

export type PaymentSourceBackend = $Keys<typeof paymentSourceBackends>;
export type PaymentSourceMethod = $Keys<typeof paymentSourceMethods>;

export type PaymentDetailsAddress = {
  addressCountry: string,
  addressState: string,
  addressZip: string,
};

export type PaymentDetailsCardFields = {
  ...$Exact<CreditCardFields>,
  backend: PaymentSourceBackend,
  brand: string,
  name: string,
};

export type PaymentDetailsPaypalFields = {
  email: string,
  token: string,
};

export type PaymentDetailsBankFields = {
  accountHolderName: string,
  accountType: string,
  addressCity: ?string,
  addressCountry: ?string,
  addressState: ?string,
  addressZip: ?string,
  backend: string,
  bankName: string,
  last4: string,
  ownershipType: string,
  routingNumber: string,
  verified: true,
};

export type TPaymentDetails<TFields> = {
  ...$Exact<PaymentDetailsAddress>,
  ...$Exact<TFields>,
};

export type PaymentDetailsCard = TPaymentDetails<PaymentDetailsCardFields>;
export type PaymentDetailsPaypal = TPaymentDetails<PaymentDetailsPaypalFields>;
export type PaymentDetailsBank = TPaymentDetails<PaymentDetailsBankFields>;

export const bankAccountTypes = Object.freeze({
  checking: 'checking',
  savings: 'savings',
});
export type BankAccountType = $Keys<typeof bankAccountTypes>;

export const bankOwnershipTypes = Object.freeze({
  business: 'business',
  personal: 'personal',
});
export type BankOwnershipType = $Keys<typeof bankOwnershipTypes>;

export type PaymentSourceDetails =
  | PaymentDetailsCard
  | PaymentDetailsPaypal
  | PaymentDetailsBank;

export type PaymentSource = {
  backend: PaymentSourceBackend,
  details: PaymentSourceDetails,
  id: string,
  type: PaymentSourceMethod,
};

export interface PaymentSourceHandler {
  save(details: Object): Promise<?Object>;
  select(data: Object): PaymentSourceDetails;
}

export type InvoiceStatus = 'refunded' | 'paid' | 'scheduled' | 'error';

export type InvoiceStatusFlags = {
  isPaid: ?boolean,
  isRefunded: ?boolean,
  isUpcoming: ?boolean,
};

export type InvoiceProps = {
  ...$Exact<InvoiceStatusFlags>,
  amountDue: number,
  createdAt: string,
  discount: ?{
    coupon: ?{
      amountOff: number,
      percentOff: number,
    },
  },
  payment: ?{
    source: ?{
      details: ?PaymentSourceDetails,
      type: PaymentSourceMethod,
    },
  },
  printableUrl: ?string,
  reason: string,
  showInvoiceColumn: boolean,
  status: InvoiceStatus,
};

export type InvoiceSectionProps = {
  ...$Exact<InvoiceProps>,
  direction: string,
  isSmall: boolean,
  status: string,
  textType: string,
};
